import { useTheme } from '@emotion/react'
import getSymbolFromCurrency from 'currency-symbol-map'
import { useStore } from 'effector-react'
import { Fragment } from 'react'

import { sessionModel } from '~/entities/session'
import { Loader } from '~/shared/ui'

import { PhoneIcon } from './assets'
import { Product, Price } from './components'
import { styles } from './styles'

interface CartProps {
  visible: boolean
  loading: boolean
}

// Cart displays cart prices and products.
export function Cart({ visible, loading }: CartProps): JSX.Element {
  const theme = useTheme()

  // get state
  const config = useStore(sessionModel.$config)

  // get selected shipping rate
  const selectedShippingRate = config?.checkout?.shippingRates?.filter(
    (rate) => rate.vendorId === config?.checkout?.selectedShippingRateId,
  )[0]

  // get currency symbol
  const currencySymbol = getSymbolFromCurrency(config?.checkout?.currency || '')
  let deliveryCustomTitlePrice
  // local pickup
  if (
    selectedShippingRate?.title?.toLowerCase() ===
    config?.checkoutSettings?.vendorLocalPickupRateTitle?.toLowerCase()
  ) {
    deliveryCustomTitlePrice =
      config?.checkoutSettings?.vendorLocalPickupRateTitle?.toUpperCase() || ''
  }
  // free shipping
  else if (
    parseFloat(selectedShippingRate?.price || '').toFixed(2) === '0.00' ||
    parseFloat(config?.checkout?.discountedDeliveryPrice || '').toFixed(2) === '0.00'
  ) {
    deliveryCustomTitlePrice = 'FREE'
  }
  return (
    <div id="cart-information-wrapper" css={styles.wrapper}>
      <div
        css={[
          styles.container.base,
          visible && styles.container.visible,
          loading && styles.container.loading,
        ]}
        data-testid="cart-information"
        onClick={(e) => e.stopPropagation()}
      >
        {loading ? (
          <Loader dataTestId="cart-loader" fill={theme.SUMMARY_BACKGROUND_COLOR} />
        ) : (
          <Fragment>
            <div css={styles.header}>
              <div css={styles.h3}>Summary</div>
              <span data-testid="cart-items_length">
                {/* lineItems count in cart */}
                {`${config?.checkout?.lineItems?.length} ${
                  (config?.checkout?.lineItems?.length || 0) > 1 ? 'items' : 'item'
                }`}
              </span>
            </div>

            <div css={styles.priceInfo}>
              <Price
                title="Subtotal"
                price={config?.checkout?.subtotalPrice}
                currencySymbol={currencySymbol}
                dataTestId="cart-subtotal_price"
              />
              <Price
                title="Delivery"
                price={selectedShippingRate?.price}
                customTextPrice={deliveryCustomTitlePrice}
                discountedPrice={config?.checkout?.discountedDeliveryPrice}
                currencySymbol={currencySymbol}
                dataTestId="cart-delivery_price"
              />
              <Price
                title="Tax"
                price={config?.checkout?.totalTaxes}
                currencySymbol={currencySymbol}
                dataTestId="cart-tax_price"
              />
              {Number(config?.checkout?.totalDiscount) > 0 && (
                <Price
                  title="Discount"
                  price={config?.checkout?.totalDiscount}
                  currencySymbol={`- ${currencySymbol}`}
                  dataTestId="cart-total_discount"
                />
              )}
              <Price
                accent
                title="Total"
                price={config?.checkout?.totalPrice}
                currencySymbol={currencySymbol}
                dataTestId="cart-total_price"
              />
            </div>

            <span>ITEMS</span>
            <div css={styles.products} data-testid="cart-items">
              {config?.checkout?.products?.map((product) => (
                <Product product={product} key={product.productId} />
              ))}
            </div>

            {config?.checkoutSettings?.needHelpEnableStatus === 'enabled' && (
              <div css={styles.container.help.container}>
                <div css={styles.h3}>{config?.checkoutSettings?.needHelpHeading}</div>
                <div css={styles.container.help.call}>
                  <PhoneIcon />
                  {config?.checkoutSettings?.needHelpTitle}
                </div>
                <span>{config?.checkoutSettings?.needHelpDescription}</span>
              </div>
            )}
          </Fragment>
        )}
      </div>
    </div>
  )
}
